import {
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import React, { Component } from "react";
import { languages } from "../../constants/languages";

/**
 * Words that stay lowercase when not the first token (Dutch / French / German / English
 * place-name articles and prepositions), e.g. "Alphen aan den Rijn", "Château sur Loire",
 * "Frankfurt am Main", "Stratford upon Avon".
 */
const PLACE_NAME_PARTICLES = new Set([
  // Dutch
  "aan",
  "bij",
  "de",
  "den",
  "der",
  "en",
  "het",
  "in",
  "op",
  "ten",
  "ter",
  "te",
  "van",
  "von",
  "uit",
  "voor",
  "over",
  "na",
  "om",
  "door",
  "tot",
  "onder",
  "tussen",
  "langs",
  "met",
  // French
  "du",
  "des",
  "le",
  "la",
  "les",
  "à",
  "en",
  "sur",
  "sous",
  "entre",
  "au",
  "aux",
  "et",
  "chez",
  "dans",
  "par",
  "pour",
  // German
  "am",
  "an",
  "die",
  "das",
  "des",
  "zu",
  "bei",
  "im",
  "zur",
  "zum",
  "für",
  "vor",
  "nach",
  "über",
  "unter",
  "zwischen",
  "durch",
  // English
  "the",
  "of",
  "on",
  "upon",
  "at",
  "by",
  "and",
  "for",
  "with",
  "near",
  "from",
  "to",
  "under",
  "between",
  "into",
]);

class SubscriptionUpsellExportContainer extends Component {
  state = {
    loading: false,
    csvData: null,
    exportedCount: 0,
    logs: [],
    error: null,
    periodStart: moment().subtract(1, "year").startOf("day"),
    periodEnd: moment().endOf("day"),
    moreThanUpgrades: 4,
  };

  addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    this.setState((prevState) => ({
      logs: [...prevState.logs, logEntry],
    }));
  };

  toUnixFromTimestamp = (value) => {
    if (!value) {
      return null;
    }
    if (value.toDate) {
      return moment(value.toDate()).unix();
    }
    if (value.seconds !== undefined) {
      return value.seconds;
    }
    if (value instanceof Date) {
      return moment(value).unix();
    }
    return null;
  };

  chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  escapeCsv = (value) => {
    const stringValue =
      value === null || value === undefined ? "" : String(value);
    const escaped = stringValue.replace(/"/g, '""');
    if (
      escaped.includes(",") ||
      escaped.includes('"') ||
      escaped.includes("\n")
    ) {
      return `"${escaped}"`;
    }
    return escaped;
  };

  /**
   * Normalizes place strings: not ALL CAPS, first word capitalized; known
   * Dutch/French/German/English particles stay lowercase except at the start.
   */
  formatPlaceName = (raw) => {
    if (raw === null || raw === undefined) {
      return "";
    }
    const s = String(raw).trim().replace(/\s+/g, " ");
    if (!s) {
      return "";
    }
    const words = s.split(" ");
    return words
      .map((word, index) => {
        const lower = word.toLowerCase();
        if (index > 0 && PLACE_NAME_PARTICLES.has(lower)) {
          return lower;
        }
        if (!lower) {
          return word;
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(" ");
  };

  upgradePaidAmount = (upgrade) => {
    const currency = (upgrade.currency || "EUR")
      .toString()
      .trim()
      .toUpperCase();
    let raw;
    if (currency !== "EUR") {
      raw =
        upgrade.paymentSettlementAmount !== undefined &&
        upgrade.paymentSettlementAmount !== null
          ? upgrade.paymentSettlementAmount
          : upgrade.settlementAmount;
    } else {
      raw =
        upgrade.price !== undefined && upgrade.price !== null
          ? upgrade.price
          : upgrade.revenue !== undefined && upgrade.revenue !== null
            ? upgrade.revenue
            : upgrade.settlementAmount;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Calculate discount percentage based on total paid amount: >= 300 → 10%, 250–299 → 15%, 201–249 → 20%, 200 or lower → 25%.
   */
  discountPercentageForTotalPaid = (totalPaid) => {
    const t = Math.round(Number(totalPaid));
    if (!Number.isFinite(t)) {
      return 20;
    }
    if (t >= 300) {
      return 10;
    }
    if (t >= 250) {
      return 15;
    }
    if (t > 200) {
      return 20;
    }
    return 25;
  };

  handleStartChange = (date) => {
    this.setState({ periodStart: date });
  };

  handleEndChange = (date) => {
    this.setState({ periodEnd: date });
  };

  handleMoreThanChange = (event) => {
    const v = event.target.value;
    if (v === "" || v === "-") {
      this.setState({ moreThanUpgrades: v });
      return;
    }
    const parsed = parseInt(v, 10);
    if (!Number.isNaN(parsed)) {
      this.setState({ moreThanUpgrades: parsed });
    }
  };

  processChunksInBatches = async ({
    chunks,
    batchSize,
    logLabel,
    fetchChunk,
    onChunkResult,
  }) => {
    const totalChunks = chunks.length;
    if (!totalChunks) {
      this.addLog(`${logLabel}: no chunks to process`);
      return;
    }

    this.addLog(
      `${logLabel}: processing ${totalChunks} chunks (batch size ${batchSize})`,
    );

    let processed = 0;
    for (let i = 0; i < totalChunks; i += batchSize) {
      const batchChunks = chunks.slice(i, i + batchSize);
      const snapshots = await Promise.all(
        batchChunks.map((chunk) => fetchChunk(chunk)),
      );

      snapshots.forEach((snapshot) => {
        onChunkResult(snapshot);
      });

      processed += batchChunks.length;
      this.addLog(`${logLabel}: ${processed}/${totalChunks} chunks done`);
    }
  };

  runExport = async () => {
    const periodStartMoment = this.state.periodStart;
    const periodEndMoment = this.state.periodEnd;

    if (!periodStartMoment || !periodEndMoment) {
      this.setState({ error: "Please select both start and end date." });
      return;
    }

    const moreThanRaw = this.state.moreThanUpgrades;
    const moreThan =
      moreThanRaw === "" || moreThanRaw === "-"
        ? NaN
        : parseInt(moreThanRaw, 10);
    if (Number.isNaN(moreThan) || moreThan < 0) {
      this.setState({
        error:
          "Please enter a valid non-negative integer for “more than X upgrades”.",
      });
      return;
    }

    const periodStart = periodStartMoment.clone().startOf("day").toDate();
    const periodEnd = periodEndMoment.clone().endOf("day").toDate();
    const periodStartUnix = periodStartMoment.clone().startOf("day").unix();
    const periodEndUnix = periodEndMoment.clone().endOf("day").unix();

    if (periodStartUnix > periodEndUnix) {
      this.setState({ error: "Start date cannot be after end date." });
      return;
    }

    this.setState({
      loading: true,
      csvData: null,
      exportedCount: 0,
      logs: [],
      error: null,
    });

    try {
      this.addLog(
        `Selected period: ${moment(periodStart).format("YYYY-MM-DD")} to ${moment(
          periodEnd,
        ).format("YYYY-MM-DD")}`,
      );
      this.addLog(
        `Including users with more than ${moreThan} paid upgrade(s) in this period`,
      );

      this.addLog("Loading paid upgrades in period...");
      const snapshot = await window.firebase
        .firestore()
        .collection("upgrades")
        .where("payed", "==", true)
        .where("timestamp", ">=", periodStart)
        .where("timestamp", "<=", periodEnd)
        .get();

      const byUid = {};

      snapshot.forEach((doc) => {
        const upgrade = doc.data();
        const uid = upgrade.uid;
        if (!uid) {
          return;
        }

        const ts = this.toUnixFromTimestamp(upgrade.timestamp);
        if (ts === null || ts < periodStartUnix || ts > periodEndUnix) {
          return;
        }

        const paid = this.upgradePaidAmount(upgrade);
        if (!byUid[uid]) {
          byUid[uid] = {
            count: 0,
            totalPaid: 0,
            lastTs: -1,
            lastPlace: "",
            lastLanguage: upgrade.language || "",
            lastEmail: "",
          };
        }
        const agg = byUid[uid];
        agg.count += 1;
        agg.totalPaid += paid;
        if (ts >= agg.lastTs) {
          agg.lastTs = ts;
          agg.lastPlace = this.formatPlaceName(upgrade.place || "");
          agg.lastLanguage = upgrade.language || agg.lastLanguage;
          agg.lastEmail = upgrade.email || "";
        }
      });

      const qualifyingUids = Object.keys(byUid).filter(
        (uid) => byUid[uid].count > moreThan,
      );
      this.addLog(
        `Aggregated ${Object.keys(byUid).length} users with paid upgrades; ${qualifyingUids.length} have more than ${moreThan} upgrade(s)`,
      );

      if (!qualifyingUids.length) {
        this.setState({
          loading: false,
          csvData: null,
          exportedCount: 0,
        });
        this.addLog("No rows to export");
        return;
      }

      this.addLog(
        "Loading userStatus to exclude subscription customers (status 2)...",
      );
      const statusChunks = this.chunkArray(qualifyingUids, 10);
      let subscriptionUids = {};
      await this.processChunksInBatches({
        chunks: statusChunks,
        batchSize: 5,
        logLabel: "User status",
        fetchChunk: (chunk) =>
          window.firebase
            .firestore()
            .collection("userStatus")
            .where(
              window.firebase.firestore.FieldPath.documentId(),
              "in",
              chunk,
            )
            .get(),
        onChunkResult: (statusSnapshot) => {
          statusSnapshot.forEach((doc) => {
            const data = doc.data() || {};
            if (data.status === 2) {
              subscriptionUids[doc.id] = true;
            }
          });
        },
      });

      const finalUids = qualifyingUids.filter((uid) => !subscriptionUids[uid]);
      const excludedCount = qualifyingUids.length - finalUids.length;
      if (excludedCount > 0) {
        this.addLog(
          `Excluded ${excludedCount} user(s) with userStatus.status === 2 (subscription)`,
        );
      }

      if (!finalUids.length) {
        this.setState({
          loading: false,
          csvData: null,
          exportedCount: 0,
        });
        this.addLog("No rows left after excluding subscription users");
        return;
      }

      const userChunks = this.chunkArray(finalUids, 10);
      let usersById = {};

      await this.processChunksInBatches({
        chunks: userChunks,
        batchSize: 5,
        logLabel: "Users",
        fetchChunk: (chunk) =>
          window.firebase
            .firestore()
            .collection("users")
            .where(
              window.firebase.firestore.FieldPath.documentId(),
              "in",
              chunk,
            )
            .get(),
        onChunkResult: (userSnapshot) => {
          userSnapshot.forEach((doc) => {
            usersById[doc.id] = { id: doc.id, ...doc.data() };
          });
        },
      });

      this.addLog(`Loaded ${Object.keys(usersById).length} user profile(s)`);

      const headers = [
        "email",
        "language",
        "numberOfUpgrades",
        "totalPaidAmount",
        "discountPercentage",
        "placeLastUpgrade",
      ];

      const rows = finalUids
        .map((uid) => {
          const agg = byUid[uid];
          const user = usersById[uid] || {};
          const email = user.email || agg.lastEmail || "";
          const language = user.registeredLang || agg.lastLanguage || "";
          return {
            uid,
            email,
            language,
            numUpgrades: agg.count,
            totalPaid: agg.totalPaid,
            discountPercentage: this.discountPercentageForTotalPaid(
              agg.totalPaid,
            ),
            place: agg.lastPlace,
          };
        })
        .sort((a, b) => b.totalPaid - a.totalPaid)
        .map((row) =>
          [
            this.escapeCsv(row.email),
            this.escapeCsv(row.language),
            this.escapeCsv(row.numUpgrades),
            this.escapeCsv(Math.round(row.totalPaid)),
            this.escapeCsv(row.discountPercentage),
            this.escapeCsv(row.place),
          ].join(","),
        );

      const csvData = [headers.join(","), ...rows].join("\n");

      this.setState({
        loading: false,
        csvData,
        exportedCount: finalUids.length,
      });
      this.addLog("Report generated and ready for download");
    } catch (error) {
      console.error("Error generating subscription upsell export:", error);
      this.setState({
        loading: false,
        error: "Failed to generate export report",
      });
      this.addLog(`Error: ${error.message}`);
    }
  };

  downloadCsv = () => {
    if (!this.state.csvData) {
      return;
    }
    const blob = new Blob([this.state.csvData], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const periodString = `${this.state.periodStart
      .clone()
      .format("YYYY-MM-DD")}_${this.state.periodEnd
      .clone()
      .format("YYYY-MM-DD")}`;
    link.setAttribute(
      "download",
      `subscription_upsell_users_${periodString}.csv`,
    );
    link.style.visibility = "hidden";
    try {
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  render() {
    return (
      <div style={{ padding: "20px" }}>
        <Typography variant="h4" gutterBottom>
          Subscription Upsell
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Export users who completed more than a chosen number of paid upgrades
          within the selected period. Users with <code>userStatus</code>{" "}
          document <code>status === 2</code> (active subscription) are omitted.
          For EUR upgrades, totals use <code>price</code>, else{" "}
          <code>revenue</code>, else <code>settlementAmount</code>. For non-EUR,{" "}
          <code>paymentSettlementAmount</code> (fallback{" "}
          <code>settlementAmount</code>), comparable EUR settlement.
        </Typography>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "baseline",
            marginBottom: 16,
          }}
        >
          <KeyboardDatePicker
            margin="normal"
            name="periodStart"
            label="Period start"
            format={languages.createTournament.dateFormat}
            cancelLabel={languages.createTournament.cancel}
            autoOk={true}
            value={this.state.periodStart}
            onChange={this.handleStartChange}
            placeholder={languages.createTournament.datePlaceholder}
            invalidLabel={languages.createTournament.invalidDateLabel}
          />
          <KeyboardDatePicker
            margin="normal"
            name="periodEnd"
            label="Period end"
            format={languages.createTournament.dateFormat}
            cancelLabel={languages.createTournament.cancel}
            autoOk={true}
            value={this.state.periodEnd}
            onChange={this.handleEndChange}
            placeholder={languages.createTournament.datePlaceholder}
            invalidLabel={languages.createTournament.invalidDateLabel}
          />
          <TextField
            margin="normal"
            label="More than X upgrades"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            value={this.state.moreThanUpgrades}
            onChange={this.handleMoreThanChange}
            helperText="Users must have strictly more than this many paid upgrades in the period (default 4 → at least 5 purchases)"
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.runExport}
            disabled={
              this.state.loading ||
              !this.state.periodStart ||
              !this.state.periodEnd
            }
          >
            {this.state.loading ? "Generating..." : "Generate report"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={this.downloadCsv}
            disabled={!this.state.csvData || this.state.loading}
          >
            Download CSV ({this.state.exportedCount})
          </Button>
        </div>
        {this.state.error && (
          <Typography color="error" style={{ marginBottom: 16 }}>
            {this.state.error}
          </Typography>
        )}
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Process Logs
            </Typography>
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                padding: "10px",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            >
              {this.state.logs.length === 0 ? (
                <div>No logs yet</div>
              ) : (
                this.state.logs.map((log, index) => (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    {log}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default SubscriptionUpsellExportContainer;
